/**
 * Disposes GPU/CPU resources held by Three.js object hierarchies so backing
 * {@link ArrayBuffer}s for {@link THREE.BufferAttribute}s can be collected.
 *
 * Ownership-aware: pass {@link DisposeSubtreeOptions.isProtected} to skip resources
 * shared via the cache, freeing an actor's private resources without nuking shared ones.
 */

import * as THREE from 'three';

/** Options controlling which resources a subtree walk disposes. */
export interface DisposeSubtreeOptions {
  /** Returns `true` for resources to PROTECT from disposal (e.g. cache-owned). Defaults to disposing all. */
  isProtected?: (resource: object) => boolean;
  /** Dedupes resources shared between nodes (e.g. cloned meshes sharing geometry). Created per call when omitted. */
  seen?: Set<object>;
}

/** Options with `seen` resolved once at the entry point so the walk allocates nothing per node. */
type ResolvedOptions = { isProtected?: (resource: object) => boolean; seen: Set<object> };

function resolve(options: DisposeSubtreeOptions): ResolvedOptions {
  return { isProtected: options.isProtected, seen: options.seen ?? new Set<object>() };
}

/** Disposes a resource once, unless already seen or protected. */
function disposeResource(resource: { dispose: () => void } | null | undefined, options: ResolvedOptions): void {
  if (!resource || options.seen.has(resource) || (options.isProtected?.(resource) ?? false)) {
    return;
  }
  options.seen.add(resource);
  resource.dispose();
}

/**
 * Disposes a material plus its texture-valued properties: `Material.dispose()` does
 * not free texture maps (they are often shared), so we walk them explicitly.
 */
function disposeMaterial(material: THREE.Material | null | undefined, options: ResolvedOptions): void {
  if (!material || options.seen.has(material) || (options.isProtected?.(material) ?? false)) {
    return;
  }
  options.seen.add(material);
  for (const value of Object.values(material)) {
    if (value instanceof THREE.Texture) {
      disposeResource(value, options);
    }
  }
  material.dispose();
}

function disposeNode(object: THREE.Object3D, options: ResolvedOptions): void {
  if (object instanceof THREE.BatchedMesh) {
    if (!(options.isProtected?.(object) ?? false)) {
      object.dispose();
    }
    return;
  }

  if (
    object instanceof THREE.Mesh ||
    object instanceof THREE.Line ||
    object instanceof THREE.LineSegments ||
    object instanceof THREE.LineLoop ||
    object instanceof THREE.Points ||
    object instanceof THREE.Sprite
  ) {
    disposeResource(object.geometry, options);
  }

  if (object instanceof THREE.SkinnedMesh) {
    object.skeleton?.dispose();
  }

  if (object instanceof THREE.Light) {
    const shadow = (object as THREE.Light & { shadow?: THREE.LightShadow }).shadow;
    shadow?.map?.dispose();
  }

  const withMaterial = object as THREE.Object3D & {
    material?: THREE.Material | THREE.Material[];
  };
  const mat = withMaterial.material;
  if (mat) {
    const materials = Array.isArray(mat) ? mat : [mat];
    for (const m of materials) {
      if (m instanceof THREE.Material) {
        disposeMaterial(m, options);
      }
    }
  }
}

function disposeSubtreePostOrder(object: THREE.Object3D, options: ResolvedOptions): void {
  const snapshot = object.children.slice();
  for (const child of snapshot) {
    disposeSubtreePostOrder(child, options);
  }
  disposeNode(object, options);
}

/** Disposes geometry, materials and their textures on a single node. Honors `isProtected`. */
export function disposeObject3DNode(object: THREE.Object3D, options: DisposeSubtreeOptions = {}): void {
  disposeNode(object, resolve(options));
}

/** Post-order dispose under `root` (children before parents). Pass `isProtected` to skip cache-owned resources. */
export function disposeObject3DSubtree(root: THREE.Object3D, options: DisposeSubtreeOptions = {}): void {
  disposeSubtreePostOrder(root, resolve(options));
}
